"use client";
import Link, { LinkProps } from "next/link";
import React, { useContext, useMemo } from "react";
import { usePathname, useRouter } from "next/navigation";
import { isRewardsPublicPath } from "@/lib/rewards-v2/routes";
import { languageContext } from "@/contexts/language-context";
import { withLang } from "@/lib/rewards-v2/i18n";

interface TransitionLinkProps extends LinkProps {
  children: React.ReactNode;
  href: string;
  className?: string;
  preserveLang?: boolean;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function pathOnly(href: string): string {
  return href.split("?")[0].split("#")[0];
}

function hashFromHref(href: string): string {
  const i = href.indexOf("#");
  return i >= 0 ? href.slice(i + 1) : "";
}

function scrollToElementById(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const offset = isRewardsV2Route(window.location.pathname) ? 72 : 0;
  const y = el.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top: y, behavior: "auto" });
}

function scrollToHash(href: string) {
  const id = hashFromHref(href);
  if (!id) return;
  scrollToElementById(id);
}

async function scrollToHashAfterNav(href: string) {
  const id = hashFromHref(href);
  if (!id) return;
  for (let i = 0; i < 12; i += 1) {
    const el = document.getElementById(id);
    if (el) {
      scrollToElementById(id);
      return;
    }
    await sleep(50);
  }
}

function isRewardsV2Route(path: string | null | undefined): boolean {
  return isRewardsPublicPath(path);
}

export const TransitionLink: React.FC<TransitionLinkProps> = ({
  children,
  href,
  onClick,
  className,
  preserveLang = true,
  ...props
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const ctx = useContext(languageContext);
  const lang = ctx?.lang ?? "fr";

  const resolvedHref = useMemo(() => {
    if (!preserveLang) return href;
    const targetIsRv2 = isRewardsV2Route(pathOnly(href));
    const sourceIsRv2 = isRewardsV2Route(pathname);
    if (!targetIsRv2 && !sourceIsRv2) return href;
    return withLang(href, lang);
  }, [href, lang, pathname, preserveLang]);

  const handleTransition = async (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    e.preventDefault();
    onClick?.(e);

    if (pathOnly(resolvedHref) === pathname) {
      const id = hashFromHref(resolvedHref);
      if (id) {
        const nextUrl = `${pathname}${window.location.search}#${id}`;
        if (`${pathname}${window.location.search}${window.location.hash}` !== nextUrl) {
          window.history.pushState(null, "", nextUrl);
        }
        scrollToHash(resolvedHref);
      }
      return;
    }

    const body = document.body;
    const isLight =
      isRewardsV2Route(pathname) || isRewardsV2Route(pathOnly(resolvedHref));
    const transitionClass = isLight ? "page-transition-light" : "page-transition";

    body.classList.add(transitionClass);

    await sleep(300);
    router.push(resolvedHref);
    await sleep(300);
    await scrollToHashAfterNav(resolvedHref);
    body.classList.remove(transitionClass);
  };

  return (
    <Link href={resolvedHref} onClick={handleTransition} className={className} {...props}>
      {children}
    </Link>
  );
};
